// src/components/PostCard.jsx
"use client";

import Link from "next/link";
import dynamic from "next/dynamic";
import { HandMetal, MessageSquare, Share2 } from "lucide-react";
import PostMenu from "@/components/PostMenu";
import ConfirmDialog from "@/components/ConfirmDialog";
import { showToast } from "@/components/ToastCenter";
import { deleteThreadOnClient } from "@/lib/deleteThreadClient";
import { useAuth } from "@/context/AuthContext";
import { getAvatarUrl } from "@/lib/avatar";
import { CardShell, CardMedia } from "@/components/card-primitives";
import ThreadGutter from "@/components/ThreadGutter";
import LinkPreviewCard from "@/components/content/LinkPreviewCard";
import LinkifiedCardText from "@/components/LinkifiedCardText";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { db } from "@/firebase/config";
import { doc, getDoc } from "@/lib/firestore-instrumented";
import { requireSignedIn } from "@/lib/authRedirect";
import { shareWithSystem } from "@/lib/nativeShare";
import { buildPlainTextSnippet, buildShareAttribution } from "@/lib/shareText";
import { usePortal } from "@/context/PortalContext";
import { getShareBranding, shareTitle } from "@/lib/portal/shareBranding.mjs";
import { removeRenderedPreviewUrl } from "@/lib/linkPreviewText.mjs";
import AuthorIdentityLine from "@/components/AuthorIdentityLine";
import { resolveContentAuthor } from "@/lib/contentAuthor.mjs";

const ShareModal = dynamic(() => import("@/components/ShareModal"), {
  ssr: false,
  loading: () => null,
});

export default function PostCard({ t, hubSlug, clampLines = 6, focused = false }) {
  // ✅ always call hooks (no optional chaining)
  const { user } = useAuth();
  const portal = usePortal();
  const shareBranding = useMemo(() => getShareBranding(portal), [portal]);
  const router = useRouter();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [likeCount, setLikeCount] = useState(() => Number(t?.likeCount ?? 0));
  const [lastLikers, setLastLikers] = useState(() =>
    Array.isArray(t?.lastLikers) ? t.lastLikers.slice(0, 3) : []
  );
  const [liked, setLiked] = useState(false);
  const [liking, setLiking] = useState(false);
  const [likeAnim, setLikeAnim] = useState(0);
  const [countAnim, setCountAnim] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);
  const [sharePayload, setSharePayload] = useState(null);

  const author = t?.author || null;
  const authorIdentity = resolveContentAuthor({
    record: t,
    author,
    pending: Boolean(t?.authorPending),
  });
  const { displayName, handle: authorHandle, profileHref } = authorIdentity;
  const avatar = authorIdentity.pending
    ? ""
    : authorIdentity.avatarUrl || getAvatarUrl({
        profile: { username_lowercase: authorIdentity.avatarSeed },
        user: { displayName },
        size: 64,
      });

  const resolvedHubSlug = hubSlug || t?.hubSlug || "";
  const when = toDateSafe(t?.lastActivityAt) || toDateSafe(t?.createdAt);
  const body = t?.text ?? t?.content ?? t?.body ?? t?.title ?? "";
  const archived = t?._archiveState === "archived";

  // Map Firestore images [{storageURL}|{src}|string] -> CardMedia items
  const mediaItems = Array.isArray(t?.images)
    ? t.images.filter((it) => typeof it === "string" || it?.storageURL || it?.src || it?.storagePath)
    : Array.isArray(t?.mediaUrls)
    ? t.mediaUrls
    : [];
  const linkPreview = t?.linkPreview || null;
  const showLinkPreview = !mediaItems.length && linkPreview && linkPreview.url;
  const renderedBody = showLinkPreview
    ? removeRenderedPreviewUrl(body, linkPreview.url)
    : body;

  const isAuthor = !!user?.uid && (t?.authorUid === user.uid || author?.uid === user.uid);
  const isAgentOwner = !!user?.uid && t?.agentOwnerUid === user.uid;
  const canDelete = !archived && (isAuthor || isAgentOwner);
  const hiddenStyle = deleted ? { display: "none" } : undefined;

  useEffect(() => {
    setLikeCount(Number(t?.likeCount ?? 0));
    setLastLikers(Array.isArray(t?.lastLikers) ? t.lastLikers.slice(0, 3) : []);
  }, [t?.likeCount, t?.lastLikers, t?.id]);

  useEffect(() => {
    let cancelled = false;
    if (!user?.uid || !t?.id || !resolvedHubSlug) {
      setLiked(false);
      return () => {
        cancelled = true;
      };
    }
    const likeDocRef = doc(
      db,
      "discussions",
      resolvedHubSlug,
      "threads",
      t.id,
      "likes",
      user.uid
    );
    getDoc(likeDocRef)
      .then((snap) => {
        if (!cancelled) {
          const status = String(snap.data()?.status || "");
          const exists = typeof snap.exists === "function" ? snap.exists() : !!snap.exists;
          setLiked(exists && status.toLowerCase() === "liked");
        }
      })
      .catch(() => {
        if (!cancelled) setLiked(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.uid, resolvedHubSlug, t?.id]);

const handleToggleLike = useCallback(async (event) => {
    event?.stopPropagation?.();
    if (archived) return;
    if (!user?.uid) {
      requireSignedIn(user, router, { intent: "like-post" });
      return;
    }
    if (!t?.id || !resolvedHubSlug) return;

    const fnUrl = process.env.NEXT_PUBLIC_SET_LIKE_STATUS_URL;
    if (!fnUrl) {
      console.warn("[PostCard] NEXT_PUBLIC_SET_LIKE_STATUS_URL not configured");
      return;
    }

    const prevLiked = liked;
    const prevCount = likeCount;
    const prevLikers = Array.isArray(lastLikers) ? lastLikers.slice() : [];
    const nextStatus = liked ? "removed" : "liked";

    setLiked(!liked);
    setLikeCount(Math.max(0, prevCount + (liked ? -1 : 1)));
    if (!liked) {
      setLikeAnim((v) => v + 1);
      setCountAnim((v) => v + 1);
    }
    setLiking(true);

    try {
      const idToken = await user.getIdToken?.(true);
      if (!idToken) throw new Error("Not signed in");

      const resp = await fetch(fnUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          idToken,
          hubSlug: resolvedHubSlug,
          threadId: t.id,
          targetType: "thread",
          status: nextStatus,
        }),
      });
      if (!resp.ok) {
        const detail = await safeJson(resp);
        throw new Error(detail?.error || `Request failed (${resp.status})`);
      }
      const data = await resp.json();
      setLiked(String(data?.status || "").toLowerCase() === "liked");
      setLikeCount(Number(data?.likeCount ?? 0));
      if (Array.isArray(data?.lastLikers)) {
        setLastLikers(data.lastLikers.slice(0, 3));
      }
    } catch (err) {
      console.error(err);
      showToast("Could not update like. Please try again.");
      setLiked(prevLiked);
      setLikeCount(prevCount);
      setLastLikers(prevLikers);
    } finally {
      setLiking(false);
    }
  }, [archived, user, router, liked, likeCount, lastLikers, resolvedHubSlug, t?.id]);

  const handleOpenComments = useCallback(
    (options = {}) => {
      // Open the Comments/Replies drawer for this thread, derive slug if missing
      let slug = t?.hubSlug;
      if (!slug && typeof window !== "undefined") {
        const parts = window.location.pathname.split("/").filter(Boolean);
        if (parts[0] === "h" && parts[1]) slug = parts[1];
      }
      const payloadBase = {
        hubSlug: slug || "",
        threadId: t?.id || "",
      };
      if (options?.openComposer) {
        window.dispatchEvent(
          new CustomEvent("open-thread-panel", {
            detail: { ...payloadBase, openComposer: true },
          })
        );
        return;
      }
      window.dispatchEvent(
        new CustomEvent("open-thread-panel", {
          detail: {
            ...payloadBase,
            openComposer: false,
            ...options,
          },
        })
      );
    },
    [t?.hubSlug, t?.id]
  );

  const buildSharePayload = useCallback(() => {
    if (!t?.id) return null;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const basePath = resolvedHubSlug
      ? `/share/h/${encodeURIComponent(resolvedHubSlug)}/thread/${encodeURIComponent(t.id)}`
      : `/h/${encodeURIComponent(resolvedHubSlug || "")}?focus=thread:${encodeURIComponent(t.id)}`;
    const url = origin ? `${origin}${basePath}` : basePath;
    const snippet = buildPlainTextSnippet(body, 140) || `Check out this discussion from ${displayName}`;
    const spaceName =
      t?.hubDisplayName ||
      t?.hubName ||
      (resolvedHubSlug ? toTitle(resolvedHubSlug) : "this space");
    const authorLabel = displayName || "A member";
    const textWithAttribution = `${snippet}

— ${buildShareAttribution(authorLabel, spaceName)}`;
    return {
      url,
      title: shareTitle(displayName, shareBranding),
      text: textWithAttribution,
    };
  }, [t?.id, resolvedHubSlug, body, displayName, shareBranding]);

  const handleShare = useCallback(async (event) => {
    event?.stopPropagation?.();
    const payload = buildSharePayload();
    if (!payload) return;
    if (await shareWithSystem(payload)) return;

    setSharePayload(payload);
    setShareOpen(true);
  }, [buildSharePayload]);

  async function handleConfirmDelete() {
    try {
      setConfirming(true);
      if (!t?.id) throw new Error("Missing post id");

      const slug = t?.hubSlug || hubSlug || "";
      const fnUrl = process.env.NEXT_PUBLIC_DELETE_THREAD_URL;

      if (fnUrl) {
        // Send token via header + body (server accepts either)
        const idToken = await user?.getIdToken?.();
        if (!idToken) throw new Error("Not signed in");

        const resp = await fetch(fnUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ idToken, hubSlug: slug, threadId: t.id }),
        });
        if (!resp.ok) {
          const j = await safeJson(resp);
          throw new Error(j?.error || `Delete failed (${resp.status})`);
        }
      } else {
        // Local/dev fallback
        await deleteThreadOnClient(slug, t.id, Array.isArray(t?.images) ? t.images : []);
      }

      setDeleted(true); // keep hook order stable; just hide the card
      setConfirmOpen(false);
      showToast("Post deleted.");
    } catch (err) {
      console.error(err);
      showToast("Delete failed. Please try again.");
    } finally {
      setConfirming(false);
    }
  }

  const hubTitle = useMemo(() => toTitle(resolvedHubSlug || "space"), [resolvedHubSlug]);

  const likeTooltip = useMemo(() => {
    if (Array.isArray(lastLikers) && lastLikers.length > 0) {
      const names = lastLikers
        .map((entry) => {
          if (!entry) return null;
          if (entry.uid === user?.uid) return "You";
          return entry.displayName || entry.username_lowercase || "Member";
        })
        .filter(Boolean);
      return names.join(", ");
    }
    return liked ? "You like this" : "";
  }, [lastLikers, liked, user?.uid]);

  return (
    <>
      <CardShell
        id={t?.id ? `thread-${t.id}` : undefined}
        className="card--square"
        bodyClassName=""
        edgeMobile
        style={hiddenStyle}
        leading={
          <ThreadGutter
            avatar={avatar}
            alt={`${displayName} avatar`}
            avatarPending={authorIdentity.pending}
            replyCount={Math.max(0, Number(t?.replyCount || 0))}
            onOpenChildren={() => handleOpenComments({ focusFirstChild: true })}
            onReply={() => handleOpenComments({ openComposer: true })}
            profileHref={profileHref}
          />
        }
        header={
          <AuthorIdentityLine identity={authorIdentity}>
            <span className="text-[var(--muted)]">· {timeAgo(when)}</span>
            <span className="text-[var(--muted)]">in</span>
            <Link
              href={resolvedHubSlug ? `/h/${resolvedHubSlug}` : "#"}
              onClick={(e) => e.stopPropagation()}
              className="truncate text-[var(--brand)] hover:underline relative z-20 pointer-events-auto"
            >
              {hubTitle}
            </Link>
            {archived ? (
              <span className="rounded-full border border-[var(--border)] px-2 py-0.5 text-xs font-medium text-[var(--muted)]">
                Archived
              </span>
            ) : null}
          </AuthorIdentityLine>
        }
        actions={
          <div className="flex w-full items-center justify-between gap-3">
            <div className="flex items-center gap-4 sm:gap-6">
              <button
                type="button"
                onClick={handleToggleLike}
                disabled={liking || archived}
                aria-pressed={liked}
                aria-label={liked ? "Unlike" : "Like"}
                data-tooltip={likeTooltip ? `Liked by ${likeTooltip}` : liked ? "Unlike" : "Like"}
                className={`action-tooltip btn-halo-icon pressable inline-flex items-center gap-2 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--card)] ${
                  liked ? "text-[var(--brand)]" : "hover:text-[var(--brand)]"
                } ${liking ? "opacity-70" : ""}`}
              >
                <span className={`icon-halo relative ${liked ? "text-[var(--brand)]" : ""}`}>
                  <HandMetal className={`h-4 w-4 ${liked ? "fill-current" : ""}`} />
                  {likeAnim > 0 ? (
                    <HandMetal
                      className="like-pop absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 text-[var(--brand)] fill-[var(--brand)]"
                      strokeWidth={2.4}
                      fill="currentColor"
                      key={likeAnim}
                      style={{ zIndex: 2 }}
                    />
                  ) : null}
                </span>
                <span
                  className={`pl-0.5 sm:pl-1 text-sm ${countAnim > 0 ? "count-flip" : ""}`}
                  key={`${likeCount}-${countAnim}`}
                >
                  {likeCount}
                </span>
              </button>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (archived) return;
                  if (!user?.uid) {
                    requireSignedIn(user, router, { intent: "reply" });
                    return;
                  }
                  handleOpenComments({ openComposer: true });
                }}
                className="action-tooltip btn-halo-icon pressable group inline-flex items-center gap-2 rounded-full hover:text-[var(--brand)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--card)]"
                disabled={archived}
                aria-label="Reply"
                data-tooltip="Reply"
              >
                <span className="icon-halo">
                  <MessageSquare className="h-4 w-4" />
                </span>
                <span className="pl-0.5 sm:pl-1">{t?.replyCount ?? 0}</span>
              </button>

              <button
                type="button"
                onClick={handleShare}
                className="action-tooltip btn-halo-icon pressable group inline-flex items-center gap-2 rounded-full hover:text-[var(--brand)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--card)]"
                aria-label="Share"
                data-tooltip="Share"
              >
                <span className="icon-halo">
                  <Share2 className="h-4 w-4" />
                </span>
                <span className="pl-0.5 sm:pl-1">Share</span>
              </button>
            </div>
          </div>
        }
        menu={
          // Only show the menu if the current user can delete
          canDelete ? (
            <div
              className="relative z-20 pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <PostMenu
                canDelete={canDelete}
                onDelete={() => setConfirmOpen(true)}
              />
            </div>
          ) : null
        }
      >
        {/* Body */}
        {renderedBody ? (
          <LinkifiedCardText
            content={renderedBody}
            clampLines={clampLines}
            expandOnFocus={focused}
            className="prose max-w-none text-[15px] leading-6 dark:prose-invert [&_a]:text-[var(--brand)] [&_a]:no-underline [&_a]:hover:text-[var(--brand)]"
          />
        ) : null}

        {/* Media */}
        {mediaItems.length > 0 ? (
          <CardMedia
            items={mediaItems}
            maxHeight={520}
            className="mt-3"
            variant={mediaItems.length > 1 ? "carousel" : "single"}
          />
        ) : showLinkPreview ? (
          <div className={renderedBody ? "mt-3" : ""}>
            <LinkPreviewCard preview={linkPreview} />
          </div>
        ) : null}
      </CardShell>

      {/* Shared confirm dialog */}
      <ConfirmDialog
        open={confirmOpen}
        confirming={confirming}
        title="Delete"
        message="Are you sure you want to delete your post?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        tone="danger"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        autoFocus="cancel"
      />

      {shareOpen ? (
        <ShareModal
          open={shareOpen}
          onClose={() => {
            setShareOpen(false);
            setSharePayload(null);
          }}
          payload={sharePayload}
        />
      ) : null}
    </>
  );
}

/* ---------------- helpers ---------------- */

function toTitle(slug = "") {
  return String(slug)
    .split("-")
    .map((s) => (s ? s[0].toUpperCase() + s.slice(1) : ""))
    .join(" ");
}

function toDateSafe(ts) {
  if (!ts) return undefined;
  if (typeof ts?.toDate === "function") return ts.toDate(); // Firestore Timestamp
  if (ts instanceof Date) return ts;
  const parsed = new Date(ts);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

function timeAgo(date) {
  if (!date) return "";
  const s = Math.floor((Date.now() - date.getTime()) / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return date.toLocaleDateString();
}

async function safeJson(resp) {
  try {
    return await resp.json();
  } catch {
    return null;
  }
}
